import React, { useMemo } from 'react';
import { CalendarOccurrence } from '../../types';
import { WEEKDAY_KO, keyParts, layoutWeek, itemsByDay, kstParts, pad2, addDaysKey } from '../../utils/calendarDate';
import { SCOPE_STYLE, chipClassOf } from './scopeStyles';

interface MonthGridProps {
  /** 월 격자 42칸 키 (calendarDate.monthGridKeys) */
  gridKeys: string[];
  /** 보고 있는 달(1~12) — 다른 달 칸은 흐리게 */
  month: number;
  occurrences: CalendarOccurrence[];
  holidays: Record<string, string>;
  today: string;
  selected: string | null;
  onSelectDay: (key: string) => void;
  /** 넓은 화면(막대) — 없으면 점 */
  onOpenOccurrence?: (occ: CalendarOccurrence) => void;
  /** 빈 칸 더블클릭 → 그 날 새 일정 (넓은 화면만) */
  onCreateAt?: (key: string) => void;
  variant: 'bars' | 'dots';
  /** 점 모드의 칸 높이 — 홈 카드는 더 촘촘하게 */
  dense?: boolean;
}

const MAX_BAR_ROWS = 3;

function dayNumberClass(key: string, today: string, selected: string | null, inMonth: boolean, isRed: boolean, isSat: boolean) {
  if (key === today) return 'bg-brand-600 text-white';
  if (key === selected) return 'bg-brand-100 text-brand-700';
  const tone = isRed ? 'text-brand-700' : isSat ? 'text-blue-700' : 'text-slate-700';
  return `${tone} ${inMonth ? '' : 'opacity-40'}`;
}

/**
 * 월 격자 — 넓은 화면은 칸 안에 막대(여러 날 일정은 한 줄로 이어 그림), 좁은 화면·홈 카드는 점.
 * 날짜·요일은 KST 고정(calendarDate) — 기기 시간대가 달라도 칸이 밀리지 않는다.
 * 색은 scopeStyles 정적 맵만 쓴다. 오늘 = brand-600 원, 선택 = brand-100(활성 틴트 규칙), 일·공휴일 = brand-700.
 */
const MonthGridComponent: React.FC<MonthGridProps> = ({
  gridKeys, month, occurrences, holidays, today, selected, onSelectDay, onOpenOccurrence, onCreateAt, variant, dense,
}) => {
  const weeks = useMemo(() => {
    const out: string[][] = [];
    for (let i = 0; i < gridKeys.length; i += 7) out.push(gridKeys.slice(i, i + 7));
    return out;
  }, [gridKeys]);

  const byDay = useMemo(
    () => (variant === 'dots' && gridKeys.length ? itemsByDay(occurrences, gridKeys[0], addDaysKey(gridKeys[gridKeys.length - 1], 1)) : {}),
    [variant, occurrences, gridKeys],
  );

  const layouts = useMemo(
    () => (variant === 'bars' ? weeks.map(w => layoutWeek(w, occurrences, MAX_BAR_ROWS)) : []),
    [variant, weeks, occurrences],
  );

  return (
    <div className="select-none" role="grid" aria-label="월간 일정">
      <div className="grid grid-cols-7" role="row">
        {WEEKDAY_KO.map((w, i) => (
          <div
            key={w}
            role="columnheader"
            className={`text-center text-xs font-semibold py-1.5 ${i === 0 ? 'text-brand-700' : i === 6 ? 'text-blue-700' : 'text-slate-500'}`}
          >
            {w}
          </div>
        ))}
      </div>

      {weeks.map((week, wi) => (
        <div key={week[0]} role="row" className={`relative ${variant === 'bars' ? 'border-t border-slate-100' : ''}`}>
          {/* 칸 — 날짜 버튼 */}
          <div className="grid grid-cols-7">
            {week.map((key, ci) => {
              const p = keyParts(key);
              const inMonth = p.m === month;
              const holiday = holidays[key];
              const isRed = ci === 0 || !!holiday;
              const isSat = ci === 6 && !holiday;
              const dayItems = variant === 'dots' ? byDay[key] || [] : null;
              const count = variant === 'dots'
                ? (dayItems as unknown[]).length
                : layouts[wi].bars.filter(b => b.col <= ci && ci < b.col + b.span).length + layouts[wi].hiddenByCol[ci];
              const label = `${p.m}월 ${p.d}일 ${WEEKDAY_KO[p.dow]}요일${holiday ? `, ${holiday}` : ''}${count ? `, 일정 ${count}개` : ''}`;
              if (variant === 'dots') {
                const scopes = Array.from(new Set((dayItems || []).map(d => d.item.source === 'intranet' ? 'dept' : d.item.scope))).slice(0, 3);
                return (
                  <button
                    key={key}
                    type="button"
                    role="gridcell"
                    aria-selected={key === selected}
                    aria-label={label}
                    onClick={() => onSelectDay(key)}
                    className={`flex flex-col items-center ${dense ? 'py-0.5' : 'py-1'} rounded-lg hover:bg-slate-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600`}
                  >
                    <span className={`${dense ? 'w-7 h-7 text-[13px]' : 'w-8 h-8 text-sm'} rounded-full flex items-center justify-center font-semibold tabular-nums ${dayNumberClass(key, today, selected, inMonth, isRed, isSat)}`}>
                      {p.d}
                    </span>
                    <span className="h-1.5 mt-0.5 flex items-center gap-0.5" aria-hidden="true">
                      {scopes.map(s => (
                        <span key={s} className={`w-1.5 h-1.5 rounded-full ${SCOPE_STYLE[s as keyof typeof SCOPE_STYLE].dot}`} />
                      ))}
                    </span>
                  </button>
                );
              }
              return (
                <div
                  key={key}
                  role="gridcell"
                  aria-selected={key === selected}
                  onDoubleClick={() => onCreateAt?.(key)}
                  className={`min-h-[7.75rem] border-l border-slate-100 first:border-l-0 ${key === selected ? 'bg-brand-100/40' : inMonth ? '' : 'bg-slate-50/60'}`}
                >
                  <button
                    type="button"
                    aria-label={label}
                    onClick={() => onSelectDay(key)}
                    className="w-full flex items-center gap-1 px-1.5 pt-1 pb-0.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600 rounded"
                  >
                    <span className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[13px] font-semibold tabular-nums ${dayNumberClass(key, today, selected, inMonth, isRed, isSat)}`}>
                      {p.d}
                    </span>
                    {holiday && <span className={`text-[11px] text-slate-500 truncate ${inMonth ? '' : 'opacity-40'}`}>{holiday}</span>}
                  </button>
                </div>
              );
            })}
          </div>

          {/* 막대 — 칸 위에 겹친 격자. 칸 버튼을 가리지 않도록 막대만 클릭을 받는다 */}
          {variant === 'bars' && (
            <div
              className="absolute inset-x-0 top-8 grid grid-cols-7 gap-y-0.5 pointer-events-none"
              style={{ gridTemplateRows: `repeat(${MAX_BAR_ROWS}, 1.375rem) 1.125rem` }}
            >
              {layouts[wi].bars.map(bar => {
                const o = bar.item;
                const multi = o.allDay || o.startDate !== o.endDate;
                const s = kstParts(o.start);
                return (
                  <button
                    key={`${o.key}:${bar.col}`}
                    type="button"
                    onClick={() => onOpenOccurrence?.(o)}
                    title={o.title}
                    style={{ gridColumn: `${bar.col + 1} / span ${bar.span}`, gridRow: bar.row + 1 }}
                    className={`pointer-events-auto mx-1 min-w-0 h-[1.375rem] px-1.5 text-left text-xs font-medium truncate flex items-center gap-1 transition-[filter] hover:brightness-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 ${
                      multi ? `${chipClassOf(o)} ${bar.continuesBefore ? 'rounded-l-none -ml-0' : 'rounded-l'} ${bar.continuesAfter ? 'rounded-r-none -mr-0' : 'rounded-r'}` : 'rounded text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    {!multi && (
                      <>
                        <span aria-hidden="true" className={`w-1.5 h-1.5 shrink-0 rounded-full ${o.source === 'intranet' ? SCOPE_STYLE.dept.dot : SCOPE_STYLE[o.scope].dot}`} />
                        <span className="shrink-0 text-slate-500 tabular-nums">{`${pad2(s.hh)}:${pad2(s.mm)}`}</span>
                      </>
                    )}
                    <span className="truncate">{o.title}</span>
                  </button>
                );
              })}
              {layouts[wi].hiddenByCol.map((n, ci) => (n > 0 ? (
                <button
                  key={`more-${ci}`}
                  type="button"
                  onClick={() => onSelectDay(week[ci])}
                  style={{ gridColumn: `${ci + 1} / span 1`, gridRow: MAX_BAR_ROWS + 1 }}
                  className="pointer-events-auto mx-1 px-1.5 text-left text-[11px] font-semibold text-slate-500 hover:text-slate-800 rounded"
                >
                  +{n}개 더
                </button>
              ) : null))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export const MonthGrid = React.memo(MonthGridComponent);
